import { NavLink, useParams } from "react-router";
import { useVbsConfirmationShowQuery } from "@/services/backend/vbs/confirmations/api/confirmation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Confirmation } from "@/services/backend/vbs/confirmations/types/confirmation";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import {
  ErrDocumentNotReadyContextProvider,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { DeleteConfirmationDialog } from "@/routes/processes/[processId]/vbs/confirmations/[confirmationId]/_components/delete-confirmation-dialog";
import { ConfirmationPdf } from "@/routes/processes/[processId]/vbs/confirmations/[confirmationId]/_components/confirmation-pdf";
import { DuplicateConfirmationButton } from "@/routes/processes/[processId]/vbs/confirmations/[confirmationId]/_components/duplicate-confirmation-button";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { hasFieldError } from "@/shared/lib/errors/validation-error";

export function ConfirmationRoute() {
  const { confirmationId } = useParams();

  const {
    data: confirmation,
    isLoading,
    error,
  } = useVbsConfirmationShowQuery({
    id: confirmationId!,
  });

  const breadcrumbs = confirmation && (
    <VbsBreadcrumbs
      customerId={confirmation.customerId}
      processId={confirmation.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink
            to={`/processes/vbs/${confirmation.processId!}/confirmations`}
          >
            {t("Auftragsbestätigungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={confirmation.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {confirmation && <ConfirmationPage confirmation={confirmation} />}
    </ProcessPageMainTemplate>
  );
}

function ConfirmationPage(props: { confirmation: Confirmation }) {
  const { confirmation } = props;

  return (
    <PersistenceContextProvider>
      <ErrDocumentNotReadyContextProvider>
        <Page confirmation={confirmation} />
      </ErrDocumentNotReadyContextProvider>
    </PersistenceContextProvider>
  );
}

function Page({ confirmation }: { confirmation: Confirmation }) {
  return (
    <DocumentPage>
      <DocumentImmutableAlert document={confirmation} />
      <DocumentPageHeader>
        <TabNavigation />
        <DocumentPageActions>
          {confirmation.immutable ? (
            <DuplicateConfirmationButton
              processId={confirmation.processId}
              confirmationId={confirmation.id}
            />
          ) : (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteConfirmationDialog confirmationId={confirmation.id} />
            </>
          )}
          <ConfirmationPdf
            confirmationId={confirmation.id}
            immutable={confirmation.immutable}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

function TabNavigation() {
  const { notReadyError } = useErrDocumentNotReady();

  const links = [
    {
      to: "data",
      hasValidationError: hasFieldError(
        notReadyError,
        "date",
        "orderDate",
        "orderReference",
        "treatmentPrice.finalPrice",
        "pricesForExtras.ratePerAccessPanel",
        "pricesForExtras.hourlyRate",
      ),
      label: t("Bestätigung"),
    },
    {
      to: "treatment",
      hasValidationError: false,
      label: t("Leistungen"),
    },
    {
      to: "calculation",
      hasValidationError: false,
      label: t("Kalkulation"),
    },
    {
      to: "addresses-and-contacts",
      hasValidationError: hasFieldError(
        notReadyError,
        "postalAddress",
        "serviceAddress",
        "representative",
      ),
      label: t("Adressen & Kontakte"),
    },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          hasValidationError={link.hasValidationError}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
